import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import './Footer.css';

const PRINCIPAL_LINKS = [
  { to: '/events', label: 'Events' },
  { to: '/garage', label: 'Garage' },
  { to: '/social', label: 'Community' },
  { to: '/privacy', label: 'Privacy & Visibility' },
];

const SUPPORT_ROWS = [
  [
    { to: '/hosting', label: 'Hosting' },
    { to: '/grouppulse', label: 'GroupPulse' },
    { to: '/pro', label: 'RidePulse Pro' },
    { to: '/getting-started', label: 'Getting Started' },
  ],
  [
    { to: '/help', label: 'Guides & Help' },
    { to: '/about', label: 'About Paul' },
    { to: '/contact', label: 'Contact' },
    { to: '/status', label: 'Status' },
  ],
];

const SOCIAL_LINKS = [
  {
    href: 'https://www.facebook.com/profile.php?id=61582424256096',
    label: 'Facebook',
    glyph: (
      <path d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z" />
    ),
  },
  {
    href: 'https://instagram.com/ridepulseapp',
    label: 'Instagram',
    glyph: (
      <>
        <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="17.3" cy="6.7" r="1.3" />
      </>
    ),
  },
];

const Footer: React.FC = () => {
  const { colors } = useTheme();

  return (
    <footer
      className="border-t"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
    >
      <div className="footer-content mx-auto max-w-6xl px-4 sm:px-6 py-[32px] lg:py-[48px]">
        <div className="footer-brand-row">
          <Link
            to="/"
            className="footer-brand-link"
            aria-label="RidePulse home"
          >
            <img
              src="/assets/images/logo-256.png"
              alt=""
              className="footer-brand-logo"
              style={{ border: `1px solid ${colors.border}` }}
            />
            <span className="footer-brand-name" style={{ color: colors.textPrimary }}>
              RidePulse
            </span>
          </Link>
          <p className="footer-tagline" style={{ color: colors.textSecondary }}>
            Find your crew.
          </p>
        </div>

        <nav aria-label="Featured">
          <ul className="footer-nav-principal">
            {PRINCIPAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="footer-principal-link"
                  style={{ color: colors.textPrimary }}
                >
                  <span className="footer-link-text">{l.label}</span>
                  <span className="footer-arrow" aria-hidden="true" style={{ color: colors.textSecondary }}>↗</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <hr className="footer-divider" style={{ borderColor: colors.border }} />

        <nav className="footer-support-nav" aria-label="More from RidePulse">
          {SUPPORT_ROWS.map((row, i) => (
            <ul key={i} className="footer-support-row">
              {row.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="footer-support-link"
                    style={{ color: colors.textSecondary }}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          ))}
        </nav>

        <div className="footer-utility">
          <p className="footer-copy" style={{ color: colors.textSecondary }}>
            © 2026 RidePulse. All rights reserved.
          </p>
          <div className="footer-legal">
            <Link
              to="/legal/privacy"
              className="footer-legal-link"
              style={{ color: colors.textSecondary }}
            >
              Privacy Policy
            </Link>
            <Link
              to="/legal/terms"
              className="footer-legal-link"
              style={{ color: colors.textSecondary }}
            >
              Terms
            </Link>
          </div>
          <div className="footer-socials">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.href}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="footer-social-link"
                style={{ color: colors.textSecondary }}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  {s.glyph}
                </svg>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
